/**
 * Сесийното състояние на устройството в localStorage — плоски ключове:
 *
 *   access_token         X — токенът на устройството/терминала
 *   account_is_personal  "true" личен · "false" общ терминал · липсва = null
 *   shared_account_name  името за ПИН екрана („Бар"/„Кухня")
 *   employee             служителят, влязъл с код — JSON { v, t, x }
 *
 * Всеки ресторант е отделен поддомейн = отделен origin с отделен localStorage,
 * така че два ресторанта не могат да си видят ключовете — изолацията идва от
 * браузъра, не от имена на ключове. (На localhost всички табове носят ЕДНО
 * име на ресторант от VITE_RESTAURANT_NAME, тоест префикси не биха изолирали
 * нищо и там.)
 *
 * `employee` е ОБЩ за всички табове/прозорци на устройството НАРОЧНО:
 * бекендът пази ЕДНА сесия за терминала — два прозореца с двама различни
 * служители са невъзможно състояние, при което единият тихо убива токена на
 * другия. По-рано той живееше в per-таб sessionStorage и точно това
 * разминаване чупеше терминала.
 */

import { clearEdgeTickets } from "./edge/edgeTicket";
import { clearCachedEdgeMode } from "./edge/edgeModeCache";
import {
  clearAllCachedUserInfo,
  clearCachedUserInfo,
} from "./userInfoCache";

// ─── Достъп до storage ──────────────────────────────────────────────────────
// Всеки достъп минава през try/catch (както в lib/theme.ts): в private mode или
// при пълна квота localStorage хвърля. Провален запис деградира до „пак ПИН
// след refresh" и НИКОГА не води до изход от акаунта.
const lsGet = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const lsSet = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage недостъпен — просто не помним */
  }
};
const lsRemove = (k: string) => {
  try {
    localStorage.removeItem(k);
  } catch {
    /* storage недостъпен */
  }
};
export const ssGet = (k: string): string | null => {
  try {
    return sessionStorage.getItem(k);
  } catch {
    return null;
  }
};
export const ssSet = (k: string, v: string) => {
  try {
    sessionStorage.setItem(k, v);
  } catch {
    /* storage недостъпен */
  }
};
export const ssRemove = (k: string) => {
  try {
    sessionStorage.removeItem(k);
  } catch {
    /* storage недостъпен */
  }
};

// ─── Ключове ────────────────────────────────────────────────────────────────
// Изнесени, защото storage listener-ът на SharedAccountGate ги сравнява по име.
export const TOKEN_KEY = "access_token";
export const EMPLOYEE_KEY = "employee";
const ACCOUNT_IS_PERSONAL_KEY = "account_is_personal";
const SHARED_ACCOUNT_NAME_KEY = "shared_account_name";

/** Маркер в sessionStorage: „този контекст носеше жив служител" (топъл/студен). */
export const CTX_KEY = "ctx";
/** Транзиентни localStorage ключове за ръкостискането „има ли жив съсед". */
export const PING_KEY = "ping";
export const PONG_KEY = "pong";

// Заварено от версията преди фикса: служителският токен в per-таб
// sessionStorage. Чете се само от еднократната миграция в readEmployeeRecord —
// може да отпадне след два релийза.
const LEGACY_SS_EMPLOYEE_KEY = "employee_token";

// ─── Токенът на терминала (X) ───────────────────────────────────────────────

export function getToken(reduxToken?: string | null): string | null {
  // ОБЩ терминал: подаденият (служителски) токен печели — той е по-новият.
  // ЛИЧЕН акаунт: ВИНАГИ localStorage — при два таба съседът може да е влязъл
  // наново и там да стои ПО-НОВИЯТ токен; таб със застоял Redux токен така
  // тихо се самолекува, вместо да гърми с 401.
  if (getAccountIsPersonal() === false && reduxToken) return reduxToken;
  return lsGet(TOKEN_KEY);
}

/** Токенът на самия терминал (X), без значение кой служител е влязъл. */
export function getTerminalToken(): string | null {
  return lsGet(TOKEN_KEY);
}

/**
 * Нов вход на устройството: токенът, видът на акаунта и името за ПИН екрана,
 * по дефиниция БЕЗ служител — нов вход с парола започва на чисто, иначе гейтът
 * би възкресил запис, вързан за стария X. Единственото място, което записва
 * тези ключове (вика се само от login.fulfilled).
 */
export function beginTerminalSession(session: {
  token: string;
  personal: boolean;
  sharedName: string | null;
}) {
  clearEmployeeRecord();
  // Кешираната самоличност (lib/userInfoCache.ts) е на предишния токен —
  // новият вход започва на чисто и я пълни наново с първия /user/info.
  clearAllCachedUserInfo();
  lsSet(TOKEN_KEY, session.token);
  lsSet(ACCOUNT_IS_PERSONAL_KEY, String(session.personal));
  if (session.sharedName) lsSet(SHARED_ACCOUNT_NAME_KEY, session.sharedName);
  else lsRemove(SHARED_ACCOUNT_NAME_KEY);
}

/**
 * ПЪЛЕН изход от устройството.
 *
 * Ключовете са ОБЩИ за всички контексти на origin-а (табове, PWA прозорци),
 * затова триенето им сваля ЦЯЛОТО устройство. Викай го САМО когато X е
 * доказано мъртъв или при изричен човешки изход с парола — никога заради 401
 * на служителски токен (виж lib/sessionRecovery.ts).
 */
export function clearToken() {
  lsRemove(TOKEN_KEY);
  lsRemove(ACCOUNT_IS_PERSONAL_KEY);
  lsRemove(SHARED_ACCOUNT_NAME_KEY);
  clearEmployeeRecord();
  // Билетите за касата (docs/edge.md) са издадени под токените, които току-що
  // изтрихме — без носител нямат смисъл да остават в localStorage. Същото за
  // кеша на самоличността и за запомнения режим облак/каса (следващият вход
  // тръгва от облака).
  clearEdgeTickets();
  clearAllCachedUserInfo();
  clearCachedEdgeMode();
  // НАРОЧНО остава `edge_last_known` (адресът на касата, `lib/edge/edgeLastKnown.ts`):
  // в него няма нито билет, нито самоличност, а без него излязъл терминал не
  // може да влезе в локален режим изобщо — §15.4 на договора.
}

// ─── Служителят (Y) на общ терминал ─────────────────────────────────────────

/**
 * Служителят, влязъл с код. `x` е отпечатък на терминалния токен, при който
 * записът е издаден: влезе ли терминалът наново с потребител+парола, X става
 * друг и записът автоматично става невалиден, без някой да е длъжен да си
 * спомни да го чисти.
 */
export interface EmployeeRecord {
  v: 1;
  /** Служителският токен Y. */
  t: string;
  /** Отпечатък на X (последните 32 знака), при който е издаден. */
  x: string;
}

const xFingerprintOf = (x: string | null): string => (x ? x.slice(-32) : "");

/** Разбор на суров запис — ползва се и от storage listener-а на гейта, който
 * разполага само с низовете от събитието. */
export function parseEmployeeRecord(raw: string | null): EmployeeRecord | null {
  if (!raw) return null;
  try {
    const rec = JSON.parse(raw) as EmployeeRecord;
    return rec && rec.v === 1 && typeof rec.t === "string" && rec.t
      ? rec
      : null;
  } catch {
    return null;
  }
}

export function writeEmployeeRecord(token: string): EmployeeRecord {
  const rec: EmployeeRecord = {
    v: 1,
    t: token,
    x: xFingerprintOf(getTerminalToken()),
  };
  lsSet(EMPLOYEE_KEY, JSON.stringify(rec));
  return rec;
}

export function readEmployeeRecord(): EmployeeRecord | null {
  // Легаси ключът от sessionStorage се КОНСУМИРА безусловно още първия път,
  // независимо дали новият запис съществува: оставен да виси в per-таб
  // sessionStorage, той не може да бъде изчистен от съседен прозорец и би
  // „възкръснал" по-късно (напр. след „Изход" от съседа) като отдавна мъртъв
  // служител, повличайки каскада от презареждания.
  const legacy = ssGet(LEGACY_SS_EMPLOYEE_KEY);
  if (legacy !== null) ssRemove(LEGACY_SS_EMPLOYEE_KEY);

  const rec = parseEmployeeRecord(lsGet(EMPLOYEE_KEY));

  if (!rec) {
    // Еднократна миграция: устройство, което се обновява по средата на смяна,
    // още носи служителския токен в sessionStorage на този таб.
    if (!legacy || !getTerminalToken()) return null;
    // Мигрираният запис значи „този контекст е бил жив" — маркираме го като
    // топъл, за да не падне до ПИН точно заради деплоя.
    ssSet(CTX_KEY, "1");
    return writeEmployeeRecord(legacy);
  }

  // Записът е от ПРЕДИШНА сесия на терминала (влизано е наново с парола).
  if (rec.x !== xFingerprintOf(getTerminalToken())) {
    lsRemove(EMPLOYEE_KEY);
    clearCachedUserInfo(rec.t);
    return null;
  }

  // Консумиран легаси токен, който съвпада с вече мигрирания запис (съседният
  // прозорец е мигрирал пръв), е същото доказателство „този таб е бил жив" —
  // без маркера при едновременен reload след деплоя всеки прозорец освен
  // първия падаше до ПИН (съседът мълчи на ping, докато сам се възстановява).
  if (legacy && rec.t === legacy) ssSet(CTX_KEY, "1");
  return rec;
}

/** Безусловно чистене — само при човешки жест („Изход") или пълен изход.
 * Кешираната самоличност на служителя (lib/userInfoCache.ts) си отива с
 * токена му — без носител не бива да възкръсва при следващ ПИН вход. */
export function clearEmployeeRecord() {
  clearCachedUserInfo(parseEmployeeRecord(lsGet(EMPLOYEE_KEY))?.t);
  lsRemove(EMPLOYEE_KEY);
  ssRemove(LEGACY_SS_EMPLOYEE_KEY);
}

/**
 * Compare-and-clear: трие служителя САМО ако ключът още държи точно този
 * токен.
 *
 * ЗАДЪЛЖИТЕЛНО по всички автоматични пътища (провалена заявка, провалено
 * възстановяване): ключът е ОБЩ, така че прозорец с мъртъв Y, който трие
 * безусловно, би изтрил ЖИВИЯ запис на съседа и би свалил и него до ПИН —
 * точно бъгът, който поправяме, само вкаран наново.
 */
export function clearEmployeeRecordIf(expected: string | null | undefined) {
  if (!expected) return;
  const rec = parseEmployeeRecord(lsGet(EMPLOYEE_KEY));
  if (rec && rec.t !== expected) return;
  clearEmployeeRecord();
}

// ─── Име на терминала и вид на акаунта ──────────────────────────────────────

/** Име на акаунта за общ достъп — за ПИН екрана и след refresh. */
export function getSharedAccountName(): string | null {
  return lsGet(SHARED_ACCOUNT_NAME_KEY);
}

export function getAccountIsPersonal(): boolean | null {
  const value = lsGet(ACCOUNT_IS_PERSONAL_KEY);
  if (value === null) return null;
  return value === "true";
}
